import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeroSection from "../../components/common/HeroSection";
import PageShell from "../../components/common/PageShell";
import ScrollReveal from "../../components/common/Reveal";
import ProductFilters from "../../components/products/ProductFilters";
import ProductGrid from "../../components/products/ProductGrid";
import ProductSearch from "../../components/products/ProductSearch";
import ProductsPagination from "../../components/products/ProductsPagination";
import ProductsStateMessage from "../../components/products/ProductsStateMessage";
import { ProductGridSkeleton } from "../../components/products/ProductSkeleton";
import SectionLoader from "../../components/common/SectionLoader";
import { PAGE_HEROES } from "../../constants/pageHeroes";
import { isWordPressConfigured } from "../../constants/wordpress";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useProductCategories } from "../../hooks/useProductCategories";
import { useProducts } from "../../hooks/useProducts";
import { scrollToElement } from "../../utils/scroll";
import { SECTION_CONTAINER } from "../../utils/layout";

function mergeProducts(existing, incoming) {
  const seen = new Set(existing.map((product) => product.id));
  const next = incoming.filter((product) => !seen.has(product.id));
  return next.length ? [...existing, ...next] : existing;
}

function Products() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [appendMode, setAppendMode] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const catalogTopRef = useRef(null);
  const shouldScrollToTopRef = useRef(false);
  const { categories, loading: categoriesLoading } = useProductCategories();

  const categorySlug = searchParams.get("category");
  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === categorySlug) || null,
    [categories, categorySlug],
  );

  const { products, total, totalPages, loading, error, refetch } = useProducts({
    page,
    categoryId: activeCategory?.id || null,
    search: debouncedSearch,
  });

  useLayoutEffect(() => {
    setPage(1);
    setDisplayProducts([]);
    setAppendMode(false);
  }, [categorySlug, debouncedSearch]);

  useEffect(() => {
    if (loading) return;

    if (!products.length) {
      if (page === 1) setDisplayProducts([]);
      return;
    }

    if (appendMode && page > 1) {
      setDisplayProducts((current) => mergeProducts(current, products));
      return;
    }

    setDisplayProducts(products);
  }, [products, loading, page, appendMode]);

  const isLoadingMore = loading && displayProducts.length > 0 && appendMode;
  const isRefreshingGrid = loading && displayProducts.length === 0 && !error;
  const hasMore = page < totalPages;

  const handleCategoryChange = (category) => {
    const next = new URLSearchParams(searchParams);
    if (category?.slug) {
      next.set("category", category.slug);
    } else {
      next.delete("category");
    }
    setSearchParams(next, { replace: true });
  };

  const handleShowMore = () => {
    if (!hasMore || loading) return;
    setAppendMode(true);
    setPage((current) => current + 1);
  };

  const scrollToProductsTop = () => {
    const target = catalogTopRef.current;
    if (!target) return;

    const rootStyles = getComputedStyle(document.documentElement);
    const navHeight = parseFloat(rootStyles.getPropertyValue("--caf-nav-height"));
    const safeTop = parseFloat(rootStyles.getPropertyValue("--caf-nav-safe-top")) || 0;
    const offset = (Number.isNaN(navHeight) ? 76 : navHeight) + safeTop + 12;

    scrollToElement(target, { offset, behavior: "auto" });
    requestAnimationFrame(() => {
      scrollToElement(target, { offset, behavior: "auto" });
    });
  };

  useEffect(() => {
    if (!shouldScrollToTopRef.current || loading || appendMode) return;

    shouldScrollToTopRef.current = false;
    scrollToProductsTop();
  }, [loading, page, appendMode, displayProducts.length]);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || loading) return;
    setAppendMode(false);
    setDisplayProducts([]);
    shouldScrollToTopRef.current = true;
    setPage(nextPage);
    scrollToProductsTop();
  };

  return (
    <PageShell>
      <HeroSection {...PAGE_HEROES.products} variant="page" />
      <div className="caf-section caf-section--light" data-reveal-managed="">
        <section
          className="caf-section__block caf-section--elevated caf-section--elevated-tight caf-page-section caf-products-catalog"
          aria-labelledby="products-catalog-title"
        >
          <h2 id="products-catalog-title" className="sr-only">
            {t("Products")}
          </h2>
          <div className={`${SECTION_CONTAINER} caf-page-section__body pb-8`}>
            {!isWordPressConfigured() ? (
              <ScrollReveal>
                <ProductsStateMessage
                  title={t("Products unavailable")}
                  message={t(
                    "WordPress API credentials are missing. Add REACT_APP_WC_CONSUMER_KEY and REACT_APP_WC_CONSUMER_SECRET to your environment file.",
                  )}
                />
              </ScrollReveal>
            ) : (
              <>
                <div ref={catalogTopRef} className="caf-products-toolbar" tabIndex={-1}>
                  <ScrollReveal>
                    <ProductSearch value={searchInput} onChange={setSearchInput} />
                  </ScrollReveal>
                  <ScrollReveal>
                    {categoriesLoading ? (
                      <SectionLoader size="sm" compact label={t("Loading categories")} />
                    ) : (
                      <ProductFilters
                        categories={categories}
                        activeCategorySlug={categorySlug}
                        onChange={handleCategoryChange}
                      />
                    )}
                  </ScrollReveal>
                </div>

                <div
                  className="caf-products-results"
                  aria-busy={loading || undefined}
                  aria-live="polite"
                >
                  {isRefreshingGrid ? (
                    <div className="caf-products-results__loading">
                      <ProductGridSkeleton count={6} />
                      <p className="caf-products-results__loading-label">
                        {t("Loading products")}
                      </p>
                    </div>
                  ) : null}

                  {!loading && error ? (
                    <ScrollReveal>
                      <ProductsStateMessage
                        title={t("Unable to load products")}
                        message={error}
                        onRetry={refetch}
                      />
                    </ScrollReveal>
                  ) : null}

                  {!isRefreshingGrid && !error && !displayProducts.length ? (
                    <ScrollReveal>
                      <ProductsStateMessage
                        title={t("No products found")}
                        message={t("There are no published products in this category yet.")}
                      />
                    </ScrollReveal>
                  ) : null}

                  {displayProducts.length ? (
                    <div
                      className={`caf-products-results__grid${
                        isLoadingMore ? " caf-products-results__grid--loading" : ""
                      }`}
                    >
                      <ProductGrid products={displayProducts} showAddToCart />
                      {isLoadingMore ? (
                        <div className="caf-products-results__overlay" role="status">
                          <SectionLoader size="sm" compact label={t("Loading products")} />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {displayProducts.length && !isRefreshingGrid ? (
                    <ProductsPagination
                      page={page}
                      totalPages={totalPages}
                      total={total}
                      displayedCount={displayProducts.length}
                      hasMore={hasMore}
                      loadingMore={isLoadingMore}
                      onShowMore={handleShowMore}
                      onPageChange={handlePageChange}
                    />
                  ) : null}
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}

export default Products;
